import { useMutation } from '@apollo/client';
import { navigate } from '@reach/router';
import React, { useContext, useState } from 'react';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import { DashboardCardLayout } from '@ui/atoms/DashboardCardLayout';
import FlexColumn from '@ui/atoms/FlexColumn';
import FolxLoader from '@ui/atoms/FolxLoader';
import HeadingText from '@ui/atoms/HeadingText';
import TextButton from '@ui/atoms/TextButton';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import { MembershipOptionCard } from '@ui/molecules/MembershipOptionCard';

import { Failure } from '@ui/organisms/ReactivateMembershipHandler';

import aligned from '@ui/styled/Aligned';
import padded from '@ui/styled/Padded';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';
import { IPaymentInfo } from '@folx-types/payment';
import { IFolxMembershipOption } from '@folx-types/shared-folx-membership-information';

import UPDATE_BILLING_INTERVAL_MUTATION from '@graphql/mutations/Subscription/UpdateMemberBillingInterval.mutation.gql';

export interface ISwitchToYearlyMembershipPageContent {
	title: string;
	termsCopy: string;
	termsCopyFreeTrial: string;
	primaryCta: string;
	secondaryCta: string;
	secondaryCtaFreeTrial: string;
	folxMembershipOptionYearly: IFolxMembershipOption;
}

interface ISwitchToYearlyMembershipProps extends IPageProps {
	pageContent: ISwitchToYearlyMembershipPageContent;
	paymentMethod?: IPaymentInfo;
	isTrial?: boolean;
}

const PaddedFlexColumn = padded(FlexColumn);
const AlignedPaddedFlexColumn = aligned(PaddedFlexColumn);

const SwitchToYearlyMembership = ({
	pageContent,
	paymentMethod,
	isTrial = false,
}: ISwitchToYearlyMembershipProps) => {
	const {
		title,
		termsCopy,
		termsCopyFreeTrial,
		primaryCta,
		secondaryCta,
		secondaryCtaFreeTrial,
		folxMembershipOptionYearly,
	} = pageContent;

	const [isProcessing, setIsProcessing] = useState(false);
	const [hasError, setHasError] = useState(false);
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const [updateBillingInterval] = useMutation(UPDATE_BILLING_INTERVAL_MUTATION, {
		client: graphQLv2Client,
	});

	const handlePrimaryClick = () => {
		setIsProcessing(true);
		updateBillingInterval({
			variables: {
				billingInterval: 'YEAR',
			},
		})
			.then(() => navigate('../'))
			.catch((e) => {
				console.error('error updating billing interval: ', e);
				setHasError(true);
			})
			.finally(() => {
				setIsProcessing(false);
			});
	};

	return isProcessing ? (
		<FolxLoader />
	) : hasError ? (
		<Failure header={title} />
	) : (
		<DashboardCardLayout>
			<HeadingText>{title}</HeadingText>
			<MembershipOptionCard content={folxMembershipOptionYearly} />
			<PaddedFlexColumn gap="xs" paddingLeft="md" paddingRight="md">
				<WYSIWYGContent
					dangerouslySetInnerHTML={{ __html: isTrial ? termsCopyFreeTrial : termsCopy }}
					textVariant="fineprint"
				/>
				<BasicText variant="smallText">
					{paymentMethod?.brand} ending in {paymentMethod?.last4}
				</BasicText>
			</PaddedFlexColumn>

			<AlignedPaddedFlexColumn gap="sm" alignItems="center">
				<Button label={primaryCta} variant="secondary" onClick={handlePrimaryClick} />
				<TextButton
					variant="text"
					underline
					label={isTrial ? secondaryCtaFreeTrial : secondaryCta}
					onClick={() => navigate('../')}
				/>
			</AlignedPaddedFlexColumn>
		</DashboardCardLayout>
	);
};

export default SwitchToYearlyMembership;
